// src/services/newsPhotoServices.js
import axios from "axios";

// Compat Vite & CRA
const _base =
  (typeof import.meta !== "undefined" &&
    import.meta.env &&
    import.meta.env.VITE_API_URL) ||
  process.env.REACT_APP_API_URL ||
  "";

const baseUrl = _base.replace(/\/$/, "");

// 🔹 Local (sharp + WebP ≤1600) — routes non /api
const newsLocalBase = `${baseUrl}/news`;

// 🔹 Rétro-compat Cloudinary (si encore utilisé) — routes /api
const newsApiBase = `${baseUrl}/api/news`;

const authHeaders = () => {
  const token = localStorage.getItem("token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

/**
 * Upload/Remplace l'image d'une News (local sharp).
 * - Côté serveur : MASTER + DISPLAY -1600 (fit: inside), auto-rotate EXIF.
 * - format (webp|jpg|png) passé en querystring — défaut "webp".
 * - Retour contrôleur : { id, image_url } (chemin /uploads/...)
 *
 * @param {number|string} newsId
 * @param {File|Blob} file
 * @param {Object} options
 * @param {"webp"|"jpg"|"png"} [options.format="webp"]
 * @param {function} [options.onUploadProgress]
 * @returns {Promise<{id:number, image_url:string}>}
 */
export const uploadNewsPhoto = async (newsId, file, options = {}) => {
  if (!newsId) throw new Error("newsId requis");
  if (!file) throw new Error("file requis");

  const { format = "webp", onUploadProgress } = options;

  const form = new FormData();
  // ⚠️ Côté serveur on a memoryUpload.single("file")
  form.append("file", file);

  try {
    const { data } = await axios.post(
      `${newsLocalBase}/${encodeURIComponent(
        newsId
      )}/photo?format=${encodeURIComponent(format)}`,
      form,
      {
        headers: {
          ...authHeaders(),
          // axios gère le Content-Type multipart
        },
        onUploadProgress,
      }
    );
    return data; // { id, image_url }
  } catch (error) {
    console.error("[uploadNewsPhoto] erreur:", error);
    throw error?.response?.data || error?.message || error;
  }
};

/**
 * Recadre/zoome l'image d'une News existante (local sharp).
 * - Si 'src' est omis, le serveur dérive le MASTER depuis image_url actuelle.
 * - x,y,width,height en pixels par rapport au MASTER.
 * - outW/outH si tu veux forcer un format cover (ex: 1200x630),
 *   sinon la sortie est ≤1600 (fit: inside).
 * - Retour : { id, image_url } (nouvelle display -1600).
 *
 * @param {number|string} newsId
 * @param {Object} crop
 * @param {number} crop.x
 * @param {number} crop.y
 * @param {number} crop.width
 * @param {number} crop.height
 * @param {"webp"|"jpg"|"png"} [crop.format]
 * @param {string} [crop.src]     - /uploads/... si tu veux recadrer une autre source
 * @param {number} [crop.outW]    - largeur de sortie (cover)
 * @param {number} [crop.outH]    - hauteur de sortie (cover)
 * @returns {Promise<{id:number, image_url:string}>}
 */
export const cropNewsPhoto = async (newsId, crop = {}) => {
  if (!newsId) throw new Error("newsId requis");

  const { x, y, width, height, format, src, outW, outH } = crop;
  const nums = [x, y, width, height];
  if (nums.some((v) => typeof v !== "number" || Number.isNaN(v))) {
    throw new Error("x, y, width, height numériques requis");
  }

  const payload = { x, y, width, height };
  if (format) payload.format = format;
  if (src) payload.src = src;
  if (typeof outW === "number") payload.outW = outW;
  if (typeof outH === "number") payload.outH = outH;

  try {
    const { data } = await axios.post(
      `${newsLocalBase}/${encodeURIComponent(newsId)}/crop`,
      payload,
      {
        headers: {
          "Content-Type": "application/json",
          ...authHeaders(),
        },
      }
    );
    return data; // { id, image_url }
  } catch (error) {
    console.error("[cropNewsPhoto] erreur:", error);
    throw error?.response?.data || error?.message || error;
  }
};

/**
 * (Optionnel) Upload via Cloudinary — rétro-compat.
 * - Route : PATCH /api/news/:id/photo
 * - Champ: "image"
 * - Retour: dépend de newsController.updateNewsPhoto
 *
 * @param {number|string} newsId
 * @param {File|Blob} file
 * @param {Object} options
 * @param {"webp"|"jpg"|"png"} [options.format="webp"]
 * @param {function} [options.onUploadProgress]
 * @returns {Promise<any>}
 */
export const uploadNewsPhotoCloudinary = async (
  newsId,
  file,
  options = {}
) => {
  if (!newsId) throw new Error("newsId requis");
  if (!file) throw new Error("file requis");

  const { format = "webp", onUploadProgress } = options;

  const form = new FormData();
  // ⚠️ Route Cloudinary: upload("news").single("image")
  form.append("image", file);

  try {
    const { data } = await axios.patch(
      `${newsApiBase}/${encodeURIComponent(
        newsId
      )}/photo?format=${encodeURIComponent(format)}`,
      form,
      {
        headers: {
          ...authHeaders(),
        },
        onUploadProgress,
      }
    );
    return data;
  } catch (error) {
    console.error("[uploadNewsPhotoCloudinary] erreur:", error);
    throw error?.response?.data || error?.message || error;
  }
};

/**
 * Helper: renvoie une URL exploitable dans <img src=...>
 * - Si commence par /uploads/ => préfixe avec baseUrl (API)
 * - Sinon renvoie tel quel (ex: Cloudinary https://...)
 *
 * @param {string} photoUrl
 * @returns {string}
 */
export const toAbsolutePhotoUrl = (photoUrl) => {
  if (!photoUrl) return "";
  if (photoUrl.startsWith("/uploads/")) {
    return `${baseUrl}${photoUrl}`;
  }
  return photoUrl;
};
